import React from 'react'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import { Document } from '@/modules/document/types'
import SimpleDrawer from '@/components/drawers/simple-drawer'
import SignatureWizard from '@/modules/signature/components/wizard/signature-wizard'
import { useCollaborationContext } from '@/modules/document/_helpers/CollaborationContext'
import { toast } from '@/modules/base/components/toast/toastConfig'
import lang from '@/lang'
import TodoService from '@/modules/todo/todoService'
import { errorCodes } from '@/modules/base/util/constants'

interface DocumentSignatureDrawerProps {
  document: Document,
  open: boolean,
  onClose: () => void,
  onSave: (todo) => void,
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  signatureRoot: {
    height: 'calc(100vh - 85px)',
  },
  signatureContent: {
    padding: 30,
    borderLeft: `1px solid ${theme.borderColors.default}`,
  },
  signatureFooter: {
    borderLeft: `1px solid ${theme.borderColors.default}`,
  },
}))

const DocumentSignatureDrawer: React.FC<DocumentSignatureDrawerProps> = (props) => {
  const classes = useStyles(props)

  const {
    document,
    open,
    onClose,
    onSave,
  } = props

  const { collaborationId } = useCollaborationContext()

  const createSignatureTodo = async(data) => {
    try {
      const todo = await TodoService.createSignatureTodo(data)
      onSave(todo)
    } catch (error) {
      let errorMessage = lang.todo.createErrorMessage
      if (error?.meta?.encryption?.[0].code === errorCodes.NOT_ALLOWED) {
        errorMessage = lang.fileUploaderSignatureEncryptionError
      }

      toast.showToast({ type: 'error', message: errorMessage })
    }
  }

  const handleSignatureWizardFinished = async(signatureData) => {
    await createSignatureTodo(signatureData)
    toast.showToast({ message: lang.transactionSendEmailSuccessMessage, type: 'success' })
    onClose?.()
  }

  return (
    <SimpleDrawer headline={lang.documents.signatureDrawerTitle(document.decodedName)} open={open} onClose={onClose}>
      <SignatureWizard
        styles={{
          root: classes.signatureRoot,
          content: classes.signatureContent,
          footer: classes.signatureFooter,
        }}
        tenantCollaborationId={collaborationId}
        onFinish={(signatureData) => {
          handleSignatureWizardFinished(signatureData)
        }}
        onAbort={onClose}
        document={document}
      />
    </SimpleDrawer>
  )
}

export default DocumentSignatureDrawer
